import { useMemo } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router-dom'

import {
  Button,
  ChevronRightIcon,
  Skeleton,
  useDelayedLoading,
} from '@buffer-mono/popcorn'

import { TemplateCard } from '~publish/components/TemplateCard/TemplateCard'
import { usePostTemplates } from '~publish/pages/Create/Templates/hooks/usePostTemplates'

import { SectionHeader } from '../SectionHeader'

import { getSlotIds } from '~publish/helpers/getSlotIds'

import styles from './GetInspiredCarousel.module.css'

type Template = ReturnType<typeof usePostTemplates>['templates'][number]

const MAX_TEMPLATES = 4

/** Random `count` items via Fisher-Yates, without mutating the input. */
function pickRandom(items: readonly Template[], count: number): Template[] {
  const copy = [...items]
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy.slice(0, count)
}

export function GetInspiredCarousel(): JSX.Element | null {
  const { templates, loading } = usePostTemplates()
  // The skeletons reveal after the CSS delay. The hook holds them mounted for
  // a minimum duration, so a response just after the reveal never cuts the
  // fade short. The skeletons only cover an empty view: templates render at
  // once when they arrive while the campaigns query still loads.
  const emptyWhileLoading = loading && templates.length === 0
  const heldLoading = useDelayedLoading(emptyWhileLoading)
  const showSkeletons = emptyWhileLoading || heldLoading

  // Shuffle once per load (templates is a stable memoized ref), so a hard
  // refresh surfaces a different set while re-renders keep the cards put.
  const visibleTemplates = useMemo(
    () => pickRandom(templates, MAX_TEMPLATES),
    [templates],
  )

  if (!showSkeletons && templates.length === 0) {
    return null
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <SectionHeader>Templates</SectionHeader>
        <Button
          as={Link}
          to="/create/templates"
          size="small"
          variant="tertiary"
          className={styles.seeAll}
          aria-label="View all templates"
        >
          View All
          <ChevronRightIcon />
        </Button>
      </div>
      {showSkeletons ? (
        <div
          className={styles.grid}
          // Skeleton visibility is announced as live status, not form output.
          // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
          role="status"
          aria-label="Loading templates"
        >
          {getSlotIds('inspired-template-skeleton', MAX_TEMPLATES).map(
            (skeletonId) => (
              <Skeleton
                key={skeletonId}
                className={styles.skeleton}
                data-testid="skeleton-placeholder"
              />
            ),
          )}
        </div>
      ) : (
        <div className={styles.grid}>
          {visibleTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              className={styles.card}
            >
              <TemplateCard.Clickable asChild>
                <Link
                  to={`/create/templates/template/${template.id}`}
                  aria-label={template.title}
                />
              </TemplateCard.Clickable>
              <TemplateCard.Icon size="md" />
              <TemplateCard.Title as="h3" size="sm" />
              <TemplateCard.Description maxLines={2} size="sm" />
            </TemplateCard>
          ))}
        </div>
      )}
    </div>
  )
}
